/**
 * 支付模块
 */

// @ts-ignore
import sheep from '@/sheep';

// #ifdef H5
// @ts-ignore
import $wxsdk from '@/sheep/libs/sdk-h5-weixin';
// #endif

// @ts-ignore
import { getRootUrl } from '@/sheep/helper';

// @ts-ignore
import PayOrderApi from '@/sheep/api/pay/order';

// @ts-ignore
import PayVirtualApi from '@/sheep/api/pay/virtual';

// @ts-ignore
import AuthApi from '@/sheep/api/member/auth';

/** 支付方式 */
type PaymentType = 'wechat' | 'alipay' | 'wallet' | 'mock';

/** 订单类型 */
type OrderType = 'goods' | 'recharge' | 'groupon' | 'vip' | 'travel' | 'coins' | 'unlock' | 'wish_lamp' | 'redeem';

/** 支付渠道 */
type PayChannel = 'wx_pub' | 'wx_lite' | 'wx_app' | 'alipay_wap' | 'alipay_app' | 'wallet' | 'mock';

/** 支付结果 */
type PayResult = 'success' | 'fail';

/**
 * Shopro 支付类
 */
export default class SheepPay {
	payment : PaymentType;
	id : string;
	orderType : OrderType;

	constructor(payment : PaymentType, orderType : OrderType, id : string) {
		this.payment = payment;
		this.id = id;
		this.orderType = orderType;
		this.payAction();
	}

	/** 执行支付 */
	payAction() {
		const payAction : Record<string, Record<PaymentType, () => void>> = {
			WechatOfficialAccount: {
				wechat: () => this.wechatOfficialAccountPay(),
				alipay: () => this.redirectPay(),
				wallet: () => this.walletPay(),
				mock: () => this.mockPay(),
			},
			WechatMiniProgram: {
				wechat: () => {
					console.log('[Pay] 微信小程序支付，orderType:', this.orderType, 'isVirtualGoods:', this.isVirtualGoods());
					// 虚拟商品使用虚拟支付
					if (this.isVirtualGoods()) {
						console.log('[Pay] 进入虚拟支付流程');
						return this.wechatVirtualPay();
					}
					console.log('[Pay] 进入普通微信支付流程');
					return this.wechatMiniProgramPay();
				},
				alipay: () => this.copyPayLink(),
				wallet: () => this.walletPay(),
				mock: () => this.mockPay(),
			},
			App: {
				wechat: () => this.wechatAppPay(),
				alipay: () => this.alipay(),
				wallet: () => this.walletPay(),
				mock: () => this.mockPay(),
			},
			H5: {
				wechat: () => this.wechatWapPay(),
				alipay: () => this.redirectPay(),
				wallet: () => this.walletPay(),
				mock: () => this.mockPay(),
			},
		};

		const platformName = sheep.$platform.name as string;
		console.log('[Pay] 当前平台:', platformName, '支付方式:', this.payment);
		return payAction[platformName]?.[this.payment]();
	}

	/**
	 * 判断是否是虚拟商品
	 * 虚拟商品包括：VIP会员、金币充值、许愿灯、解锁功能、心愿记录、心愿达成
	 */
	isVirtualGoods() : boolean {
		const virtualTypes : OrderType[] = ['vip', 'recharge', 'coins', 'unlock', 'wish_lamp', 'travel', 'redeem'];
		return virtualTypes.includes(this.orderType);
	}

	/**
	 * 预支付
	 * @param channel 支付渠道
	 */
	prepay(channel : PayChannel) : Promise<any> {
		return new Promise(async (resolve, reject) => {
			const data : any = {
				id: this.id,
				channelCode: channel,
				channelExtras: {},
			};

			// 特殊逻辑：微信公众号、小程序支付时，必须传入 openid
			if (['wx_pub', 'wx_lite'].includes(channel)) {
				// @ts-ignore
				const isLogin = sheep.$store('user').isLogin;
				let openid = '';
				if (isLogin) {
					openid = await sheep.$platform.useProvider('wechat').getOpenid();
					if (!openid) {
						this.bindWeixin();
						return;
					}
				} else {
					// 匿名支付：通过 wx.login 的 code 换取 openid（不创建账号）
					openid = await getAnonOpenid();
					if (!openid) {
						sheep.$helper.toast('获取支付身份失败，请重试');
						return;
					}
				}
				data.channelExtras.openid = openid;
			}

			// 发起预支付 API 调用
			PayOrderApi.submitOrder(data).then((res : any) => {
				if (res.code === 0) {
					resolve(res);
				}
				if (res.code !== 0 && res.msg?.indexOf('无效的openid') >= 0) {
					this.bindWeixin();
				}
			});
		});
	}

	// #ifdef H5
	/** 微信公众号 JSSDK 支付 */
	async wechatOfficialAccountPay() {
		const { code, data } = await this.prepay('wx_pub');
		if (code !== 0) return;

		const payConfig = JSON.parse(data.displayContent);
		$wxsdk.wxpay(payConfig, {
			success: () => {
				this.payResult('success');
			},
			cancel: () => {
				sheep.$helper.toast('支付已手动取消');
			},
			fail: (error : any) => {
				if (error.errMsg?.indexOf('chooseWXPay:没有此SDK或暂不支持此SDK模拟') >= 0) {
					sheep.$helper.toast('发起微信支付失败，原因：可能是微信开发者工具不支持，建议使用微信打开网页后支付');
					return;
				}
				this.payResult('fail');
			},
		});
	}

	/** 浏览器微信 H5 支付 */
	async wechatWapPay() {
		const { error, data } = await this.prepay('wx_wap');
		if (error === 0) {
			const redirect_url = `${getRootUrl()}pages-sub/pay/result?id=${this.id}&payment=${this.payment}&orderType=${this.orderType}`;
			location.href = `${data.pay_data.h5_url}&redirect_url=${encodeURIComponent(redirect_url)}`;
		}
	}

	/** 支付链接(支付宝 wap 支付) */
	async redirectPay() {
		const { code, data } = await this.prepay('alipay_wap');
		if (code !== 0) return;
		location.href = data.displayContent;
	}
	// #endif

	/** 微信小程序支付 */
	async wechatMiniProgramPay() {
		const { code, data } = await this.prepay('wx_lite');
		if (code !== 0) return;

		const payConfig = JSON.parse(data.displayContent);
		uni.requestPayment({
			provider: 'wxpay',
			timeStamp: payConfig.timeStamp,
			nonceStr: payConfig.nonceStr,
			package: payConfig.packageValue,
			signType: payConfig.signType,
			paySign: payConfig.paySign,
			success: () => {
				this.payResult('success');
			},
			fail: (err) => {
				if (err.errMsg === 'requestPayment:fail cancel') {
					sheep.$helper.toast('支付已手动取消');
				} else {
					this.payResult('fail');
				}
			},
		});
	}

	/** 微信小程序虚拟支付 */
	async wechatVirtualPay() {
		console.log('[VirtualPay] 开始虚拟支付流程');
		// #ifdef MP-WEIXIN
		// 1. 支付前刷新 session_key（虚拟支付签名需要，session_key 有有效期需定期刷新）
		const openid = await refreshSessionKey();
		console.log('[VirtualPay] openid:', openid);
		if (!openid) {
			sheep.$helper.toast('获取支付身份失败，请重试');
			return;
		}

		// 2. 获取订单信息
		const { data: orderInfo } = await PayOrderApi.getOrder(this.id, true);
		console.log('[VirtualPay] 订单信息:', orderInfo);
		if (!orderInfo) {
			sheep.$helper.toast('订单不存在');
			return;
		}

		// 3. 调用后端获取虚拟支付签名
		// buyQuantity=1（购买1个道具），goodsPrice 单位为分
		const goodsPriceFen = orderInfo.price as number;
		console.log('[VirtualPay] 请求虚拟支付签名，orderId:', this.id, 'openid:', openid, 'goodsPrice(分):', goodsPriceFen);
		const { code, data: signData } = await PayVirtualApi.getVirtualPaySignature({
			orderId: Number(this.id),
			openid: openid,
			goodsName: this.getGoodsName(),
			buyQuantity: 1,
			goodsPrice: goodsPriceFen,
		});
		console.log('[VirtualPay] 签名响应:', code, signData);

		if (code !== 0 || !signData) {
			sheep.$helper.toast('获取支付信息失败');
			return;
		}

		// 4. 调用微信虚拟支付 API
		// @ts-ignore
		if (typeof wx.requestVirtualPayment !== 'function') {
			console.log('[VirtualPay] wx.requestVirtualPayment 不可用');
			sheep.$helper.toast('当前微信版本不支持虚拟支付，请升级微信');
			return;
		}

		console.log('[VirtualPay] 调用 wx.requestVirtualPayment');
		// @ts-ignore
		wx.requestVirtualPayment({
			mode: 'short_series_goods',
			signData: signData.signData,
			signature: signData.signature,
			paySig: signData.paySig,
			success: () => {
				this.payResult('success');
			},
			fail: (err : any) => {
				if (err.errMsg?.includes('cancel')) {
					sheep.$helper.toast('支付已取消');
				} else {
					console.error('虚拟支付失败:', err);
					this.payResult('fail');
				}
			},
		});
		// #endif

		// #ifndef MP-WEIXIN
		sheep.$helper.toast('虚拟支付仅支持微信小程序');
		// #endif
	}

	/**
	 * 获取商品名称（用于虚拟支付显示）
	 */
	getGoodsName() : string {
		const nameMap : Record<OrderType, string> = {
			vip: 'VIP会员',
			recharge: '充值',
			coins: '金币',
			unlock: '解锁功能',
			wish_lamp: '许愿灯',
			travel: '心愿记录',
			redeem: '心愿达成',
			goods: '商品',
			groupon: '团购',
		};
		return nameMap[this.orderType] || '商品';
	}

	/** 余额支付 */
	async walletPay() {
		const { code } = await this.prepay('wallet');
		if (code === 0) this.payResult('success');
	}

	/** 模拟支付 */
	async mockPay() {
		const { code } = await this.prepay('mock');
		if (code === 0) this.payResult('success');
	}

	/** 支付宝复制链接支付 */
	async copyPayLink() {
		const { code, data } = await this.prepay('alipay_wap');
		if (code !== 0) return;

		uni.showModal({
			title: '支付宝支付',
			content: '复制链接到外部浏览器',
			confirmText: '复制链接',
			success: (res) => {
				if (res.confirm) {
					sheep.$helper.copyText(data.displayContent);
				}
			},
		});
	}

	/** 支付宝支付（App） */
	async alipay() {
		const { code, data } = await this.prepay('alipay_app');
		if (code !== 0) return;

		uni.requestPayment({
			provider: 'alipay',
			orderInfo: data.displayContent,
			success: () => {
				this.payResult('success');
			},
			fail: (err) => {
				if (err.errMsg === 'requestPayment:fail [paymentAlipay:62001]user cancel') {
					sheep.$helper.toast('支付已手动取消');
				} else {
					this.payResult('fail');
				}
			},
		});
	}

	/** 微信支付（App） */
	async wechatAppPay() {
		const { code, data } = await this.prepay('wx_app');
		if (code !== 0) {
			sheep.$helper.toast('获取支付信息失败');
			return;
		}

		let payConfig = JSON.parse(data.displayContent);
		if (typeof payConfig.appId === 'undefined') {
			payConfig.appId = payConfig.appid;
		}
		if (typeof payConfig.nonceStr === 'undefined') {
			payConfig.nonceStr = payConfig.noncestr;
		}
		if (typeof payConfig.timeStamp === 'undefined') {
			payConfig.timeStamp = payConfig.timestamp;
		}

		uni.requestPayment({
			provider: 'wxpay',
			timeStamp: payConfig.timeStamp,
			nonceStr: payConfig.nonceStr,
			package: payConfig.packageValue,
			signType: payConfig.signType,
			paySign: payConfig.paySign,
			success: () => {
				this.payResult('success');
			},
			fail: (err) => {
				if (err.errMsg === 'requestPayment:fail cancel') {
					sheep.$helper.toast('支付已手动取消');
				} else {
					sheep.$helper.toast('支付失败：' + err.errMsg);
					this.payResult('fail');
				}
			},
		});
	}

	/** 支付结果跳转 */
	payResult(resultType : PayResult) {
		goPayResult(this.id, this.orderType, resultType);
	}

	/** 引导绑定微信 */
	bindWeixin() {
		goBindWeixin();
	}
}

/** 支付方式配置 */
interface PayMethod {
	icon : string;
	title : string;
	value : PaymentType | 'apple';
	disabled : boolean;
}

/**
 * 获取支付方式列表
 * @param channels 支持的渠道
 */
export function getPayMethods(channels : string[]) : PayMethod[] {
	const payMethods : PayMethod[] = [
		{ icon: '/static/icons/pay/wechat.png', title: '微信支付', value: 'wechat', disabled: true },
		{ icon: '/static/icons/pay/alipay.png', title: '支付宝支付', value: 'alipay', disabled: true },
		// { icon: '/static/icons/pay/wallet.png', title: '余额支付', value: 'wallet', disabled: true },
		// { icon: '/static/icons/pay/apple.png', title: 'Apple Pay', value: 'apple', disabled: true },
		// { icon: '/static/icons/pay/wallet.png', title: '模拟支付', value: 'mock', disabled: true },
	];

	// @ts-ignore
	const platform = sheep.$platform.name;

	// 微信支付
	const wechatMethod = payMethods[0];
	if (
		(platform === 'WechatOfficialAccount' && channels.includes('wx_pub')) ||
		(platform === 'WechatMiniProgram' && channels.includes('wx_lite')) ||
		(platform === 'App' && channels.includes('wx_app')) ||
		(platform === 'H5' && channels.includes('wx_wap'))
	) {
		wechatMethod.disabled = false;
	}

	// 支付宝支付
	const alipayMethod = payMethods[1];
	if (
		(platform === 'H5' && channels.includes('alipay_wap')) ||
		(platform === 'WechatOfficialAccount' && channels.includes('alipay_wap')) ||
		(platform === 'WechatMiniProgram' && channels.includes('alipay_wap')) ||
		(platform === 'App' && channels.includes('alipay_app'))
	) {
		alipayMethod.disabled = false;
	}

	// 余额支付
	const walletMethod = payMethods[2];
	if (channels.includes('wallet')) {
		walletMethod.disabled = false;
	}

	// 模拟支付
	const mockMethod = payMethods[4];
	if (channels.includes('mock')) {
		mockMethod.disabled = false;
	}

	return payMethods;
}

/**
 * 支付结果跳转
 * @param id 订单ID
 * @param orderType 订单类型
 * @param resultType 支付结果
 */
export function goPayResult(id : string, orderType : OrderType, resultType : PayResult) {
	// @ts-ignore
	sheep.$router.redirect('/pages-sub/pay/result', {
		id,
		orderType,
		payState: resultType,
	});
}

/**
 * 引导绑定微信
 */
export function goBindWeixin() {
	uni.showModal({
		title: '微信支付',
		content: '请先绑定微信再使用微信支付',
		success: function (res) {
			if (res.confirm) {
				// @ts-ignore
				sheep.$platform.useProvider('wechat').bind();
			}
		},
	});
}

/**
 * 获取微信支付渠道码
 */
export function getWeixinPayChannelCode() : string {
	// @ts-ignore
	const platform = sheep.$platform.name;
	switch (platform) {
		case 'WechatOfficialAccount':
			return 'wx_pub';
		case 'WechatMiniProgram':
			return 'wx_lite';
		case 'App':
			return 'wx_app';
		case 'H5':
			return 'wx_wap';
		default:
			return '';
	}
}

/**
 * 匿名获取微信 openid（不创建账号），带本地缓存
 * 通过 wx.login 的 code 调用后端免登录接口换取
 */
async function getAnonOpenid() : Promise<string> {
	const cached = uni.getStorageSync('anonOpenid');
	if (cached) return cached as string;
	const loginRes : any = await new Promise((resolve) => {
		uni.login({ provider: 'weixin', success: resolve, fail: resolve });
	});
	const code = loginRes?.code;
	if (!code) return '';
	try {
		const res = await AuthApi.wxOpenid(code);
		if (res.code === 0 && res.data?.openid) {
			uni.setStorageSync('anonOpenid', res.data.openid);
			return res.data.openid;
		}
	} catch (e) {
		console.error('匿名获取 openid 失败:', e);
	}
	return '';
}

/**
 * 支付前刷新 session_key（虚拟支付签名需要）
 * 每次支付前都调用 wx.login 获取新 code，再调后端刷新 Redis 中的 session_key
 * 因为 session_key 有有效期（微信侧几天会更新），必须保证支付时有效
 */
async function refreshSessionKey() : Promise<string> {
	const loginRes : any = await new Promise((resolve) => {
		uni.login({ provider: 'weixin', success: resolve, fail: resolve });
	});
	const code = loginRes?.code;
	if (!code) return '';
	try {
		const res = await AuthApi.wxOpenid(code);
		if (res.code === 0 && res.data?.openid) {
			return res.data.openid as string;
		}
	} catch (e) {
		console.error('[VirtualPay] 刷新 session_key 失败:', e);
	}
	return '';
}